'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNotificationStore, type DigestPreference } from '../../model/notification-store';
import type { NotificationThread } from '../../lib/threading';
import { digestSentence, splitAtMarker, type NotificationDay } from '../../lib/days';
import { NotificationRow } from './notification-row';
import { ReadMarker } from './read-marker';

/**
 * A day, and the digest it becomes when it closes.
 *
 * Today is a list, because it is not finished. Every day whose digest hour has
 * passed is a heading, a sentence and a count, and it opens to its rows on a
 * click. That is the digest as a first-class object rather than a summary
 * bolted on top: the digest IS the day's row in the centre, so there is no
 * separate digest surface to keep in step with the list, and marking something
 * done inside an open digest changes the sentence the next time it is written.
 *
 * With the digest switched off in preferences a closed day still collapses,
 * it just loses the sentence and keeps the count. That difference is the whole
 * of what the preference buys, and it is visible on screen rather than in an
 * email nobody can see from here.
 */
export function DaySection(t0) {
  const $ = _c(40);
  const {
    day,
    digest,
    markerAt,
    onFire,
    dense: t1,
    defaultOpen
  } = t0;
  const dense = t1 === undefined ? false : t1;
  const [open, setOpen] = useState(defaultOpen ?? !day.closed);
  const markThreadRead = useNotificationStore(_temp);
  let t2;
  if ($[0] !== day.threads || $[1] !== markerAt) {
    t2 = markerAt !== null && day.threads.some(t_0 => new Date(t_0.head.createdAt).getTime() <= markerAt.getTime()) && day.threads.some(t => new Date(t.head.createdAt).getTime() > markerAt.getTime());
    $[0] = day.threads;
    $[1] = markerAt;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const markerInsideDay = t2;
  let t3;
  if ($[3] !== day || $[4] !== dense || $[5] !== digest || $[6] !== markThreadRead || $[7] !== markerAt || $[8] !== markerInsideDay || $[9] !== onFire || $[10] !== open) {
    const {
      fresh,
      seen
    } = markerInsideDay ? splitAtMarker(day.threads, markerAt) : {
      fresh: day.threads,
      seen: [] as readonly NotificationThread[]
    };
    let t4;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = () => setOpen(_temp2);
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    const t5 = open && "rotate-90";
    let t6;
    if ($[13] !== t5) {
      t6 = cn("mt-0.5 h-3.5 w-3.5 shrink-0 text-[#44546F] dark:text-white/70 transition-transform", t5);
      $[13] = t5;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    let t7;
    if ($[15] !== t6) {
      t7 = <ChevronRight className={t6} />;
      $[15] = t6;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    let t8;
    if ($[17] !== day.label) {
      t8 = <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#091E42] dark:text-white">{day.label}</span>;
      $[17] = day.label;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    let t9;
    if ($[19] !== day.unreadCount) {
      t9 = day.unreadCount > 0 ? <span className="text-[11px] text-[#44546F] dark:text-white/70">{day.unreadCount} not seen</span> : null;
      $[19] = day.unreadCount;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    let t10;
    if ($[21] !== day.count) {
      t10 = <span className="ml-auto shrink-0 rounded-full bg-white dark:bg-white/12 px-2 text-[11px] font-medium tabular-nums text-[#091E42] dark:text-white shadow-[0_1px_3px_rgba(15,23,42,0.08)]">{day.count}</span>;
      $[21] = day.count;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    let t11;
    if ($[23] !== t10 || $[24] !== t8 || $[25] !== t9) {
      t11 = <span className="flex items-baseline gap-2">{t8}{t9}{t10}</span>;
      $[23] = t10;
      $[24] = t8;
      $[25] = t9;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    let t12;
    if ($[27] !== day || $[28] !== digest || $[29] !== open) {
      t12 = day.closed && !open ? <span className="mt-1 block text-[12px] leading-relaxed text-[#44546F] dark:text-white/70">{digest.enabled ? digestSentence(day, digest) : `${day.count} ${day.count === 1 ? "thing" : "things"}. The digest is off, so this day is a count only.`}</span> : null;
      $[27] = day;
      $[28] = digest;
      $[29] = open;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    let t13;
    if ($[31] !== day.liftedCount) {
      t13 = day.liftedCount > 0 ? <span className="mt-1 block text-[11px] text-rose-600 dark:text-rose-400">{day.liftedCount} from this day {day.liftedCount === 1 ? "is" : "are"} carried above and not counted here.</span> : null;
      $[31] = day.liftedCount;
      $[32] = t13;
    } else {
      t13 = $[32];
    }
    let t14;
    if ($[33] !== t11 || $[34] !== t12 || $[35] !== t13) {
      t14 = <span className="min-w-0 flex-1">{t11}{t12}{t13}</span>;
      $[33] = t11;
      $[34] = t12;
      $[35] = t13;
      $[36] = t14;
    } else {
      t14 = $[36];
    }
    let t15;
    if ($[37] !== t14 || $[38] !== t7) {
      t15 = <button type="button" onClick={t4} className="flex w-full items-start gap-2 rounded-2xl px-3 py-2.5 text-left transition-colors hover:bg-white/60 dark:hover:bg-white/6">{t7}{t14}</button>;
      $[37] = t14;
      $[38] = t7;
      $[39] = t15;
    } else {
      t15 = $[39];
    }
    t3 = <section className="rounded-2xl bg-white shadow-[0_1px_3px_rgba(15,23,42,0.05)] dark:bg-white/12">{t15}{open ? <div className="px-1.5 pb-1.5">{fresh.map(thread => <NotificationRow key={thread.key} thread={thread} dense={dense} clock={true} onFire={thread.head.clientId !== null && onFire.has(thread.head.clientId)} />)}{markerInsideDay ? <ReadMarker at={markerAt} className="px-2" /> : null}{seen.map(thread_0 => <NotificationRow key={thread_0.key} thread={thread_0} dense={dense} clock={true} onFire={thread_0.head.clientId !== null && onFire.has(thread_0.head.clientId)} />)}{day.closed && day.unreadCount > 0 ? <button type="button" onClick={() => day.threads.forEach(t_1 => markThreadRead(t_1.key))} className="mt-1 flex w-full items-center justify-center rounded-xl px-3 py-1.5 text-[11px] font-medium text-[#44546F] dark:text-white/70 transition-colors hover:bg-white dark:hover:bg-white/12 hover:text-[#091E42] dark:hover:text-white">Mark this whole day as seen</button> : null}</div> : null}</section>;
    $[3] = day;
    $[4] = dense;
    $[5] = digest;
    $[6] = markThreadRead;
    $[7] = markerAt;
    $[8] = markerInsideDay;
    $[9] = onFire;
    $[10] = open;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
function _temp2(v) {
  return !v;
}
function _temp(s) {
  return s.markThreadRead;
}